import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import StudentHeader from '@/components/student/StudentHeader';

import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  Clock, CheckCircle, XCircle, AlertCircle, Play, ArrowLeft, ArrowRight,
  Flag, Send, Timer, Award, Target, BookOpen, RotateCcw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OnlineExamPage = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useContext(AuthContext);
  
  const [loading, setLoading] = useState(true);
  const [examData, setExamData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [attemptId, setAttemptId] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [examStarted, setExamStarted] = useState(false);
  const [examSubmitted, setExamSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  
  const timerRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please login to take exam');
      navigate('/auth/login');
      return;
    }
    startExam();
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [examId, isAuthenticated]);

  const startExam = async () => {
    try {
      const res = await axios.post(`${API}/student/exams/${examId}/start`);
      setExamData(res.data.exam);
      setQuestions(res.data.questions);
      setAttemptId(res.data.attempt.id);
      
      // Set timer for first question
      setTimeLeft(res.data.exam.time_per_question);
      setQuestionStartTime(Date.now());
      setExamStarted(true);
      
      // If resumed, restore answers
      if (res.data.resumed && res.data.attempt.answers) {
        const restoredAnswers = {};
        res.data.attempt.answers.forEach(a => {
          restoredAnswers[a.question_id] = a.selected_option;
        });
        setAnswers(restoredAnswers);
      }
      
      setLoading(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start exam');
      setLoading(false);
    }
  };

  // Timer effect
  useEffect(() => {
    if (!examStarted || examSubmitted) return;
    
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Auto-move to next question or submit
          handleTimeUp();
          return examData?.time_per_question || 60;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timerRef.current);
  }, [examStarted, examSubmitted, currentQuestionIndex]);

  const handleTimeUp = useCallback(async () => {
    const currentQ = questions[currentQuestionIndex];
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    
    // Save current answer if any
    if (answers[currentQ?.id]) {
      await saveAnswer(currentQ.id, answers[currentQ.id], timeSpent);
    }
    
    // Move to next question or submit
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setQuestionStartTime(Date.now());
      setTimeLeft(examData?.time_per_question || 60);
    } else {
      // Last question - auto submit
      handleSubmitExam();
    }
  }, [currentQuestionIndex, questions, answers, questionStartTime, examData]);

  const saveAnswer = async (questionId, selectedOption, timeSpent) => {
    try {
      await axios.post(`${API}/student/exams/save-answer`, {
        attempt_id: attemptId,
        question_id: questionId,
        selected_option: selectedOption,
        time_spent: timeSpent
      });
    } catch (error) {
      console.error('Failed to save answer:', error);
    }
  };

  const handleSelectOption = async (option) => {
    const currentQ = questions[currentQuestionIndex];
    setAnswers(prev => ({ ...prev, [currentQ.id]: option }));
    
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    await saveAnswer(currentQ.id, option, timeSpent);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setQuestionStartTime(Date.now());
      setTimeLeft(examData?.time_per_question || 60);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setQuestionStartTime(Date.now());
      setTimeLeft(examData?.time_per_question || 60);
    }
  };

  const jumpToQuestion = (index) => {
    setCurrentQuestionIndex(index);
    setQuestionStartTime(Date.now());
    setTimeLeft(examData?.time_per_question || 60);
  };

  const toggleFlag = () => {
    const currentQ = questions[currentQuestionIndex];
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(currentQ.id)) {
        newSet.delete(currentQ.id);
      } else {
        newSet.add(currentQ.id);
      }
      return newSet;
    });
  };

  const handleSubmitExam = async () => {
    if (!window.confirm('Are you sure you want to submit the exam?')) return;
    
    try {
      const res = await axios.post(`${API}/student/exams/submit`, {
        attempt_id: attemptId
      });
      
      setExamSubmitted(true);
      setResult(res.data);
      if (timerRef.current) clearInterval(timerRef.current);
      
      toast.success('Exam submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit exam');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading exam...</p>
        </div>
      </div>
    );
  }

  // Show result after submission
  if (examSubmitted && result) {
    return (
      <div className="min-h-screen bg-slate-50">
        <StudentHeader />
        <div className="max-w-2xl mx-auto py-12 px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="exam-result">
            {result.result_pending ? (
              <>
                <Clock className="h-16 w-16 mx-auto text-blue-600 mb-4" />
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Exam Submitted!</h2>
                <p className="text-slate-600 mb-6">
                  Results will be announced after the exam ends for all students.
                </p>
                <Button onClick={() => navigate('/student/dashboard')} className="bg-blue-600 hover:bg-blue-700">
                  Back to Dashboard
                </Button>
              </>
            ) : (
              <>
                <div className={`h-20 w-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
                  result.passed ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {result.passed ? (
                    <Award className="h-10 w-10 text-green-600" />
                  ) : (
                    <XCircle className="h-10 w-10 text-red-600" />
                  )}
                </div>
                
                <h2 className={`text-3xl font-bold mb-2 ${result.passed ? 'text-green-600' : 'text-red-600'}`}>
                  {result.passed ? 'Congratulations!' : 'Better Luck Next Time!'}
                </h2>
                <p className="text-slate-600 mb-6">
                  {result.passed ? 'You have passed the exam!' : 'You did not pass this time.'}
                </p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-2xl font-bold text-blue-600">{result.marks_obtained}</p>
                    <p className="text-sm text-slate-500">Marks Obtained</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-2xl font-bold text-slate-600">{result.total_marks}</p>
                    <p className="text-sm text-slate-500">Total Marks</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className={`text-2xl font-bold ${result.percentage >= 40 ? 'text-green-600' : 'text-red-600'}`}>
                      {result.percentage?.toFixed(1)}%
                    </p>
                    <p className="text-sm text-slate-500">Percentage</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-2xl font-bold text-green-600">{result.correct_answers}</p>
                    <p className="text-sm text-slate-500">Correct</p>
                  </div>
                </div>
                
                {/* Certificate notification for passed students */}
                {result.passed && result.certificate_id && (
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-center gap-2 text-amber-700">
                      <Award className="h-6 w-6" />
                      <span className="font-semibold">Certificate Generated!</span>
                    </div>
                    <p className="text-amber-600 text-sm mt-1">
                      Your certificate has been automatically generated.
                    </p>
                  </div>
                )}
                
                <div className="flex gap-4 justify-center flex-wrap">
                  <Button variant="outline" onClick={() => navigate('/student/dashboard')}>
                    Back to Dashboard
                  </Button>
                  {result.passed && result.certificate_id && (
                    <Button 
                      className="bg-amber-500 hover:bg-amber-600"
                      onClick={() => navigate('/student/certificates')}
                      data-testid="view-certificate-btn"
                    >
                      <Award className="h-4 w-4 mr-2" />
                      View Certificate
                    </Button>
                  )}
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => navigate('/student/exams')}
                  >
                    View All Results
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
        
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Exam Header */}
      <div className="bg-slate-800 text-white py-3 px-4 sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="font-bold text-lg">{examData?.title}</h1>
            <p className="text-sm text-slate-300">{examData?.course_name}</p>
          </div>
          
          <div className="flex items-center gap-6">
            {/* Timer */}
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              timeLeft <= 10 ? 'bg-red-600 animate-pulse' : 'bg-slate-700'
            }`}>
              <Timer className="h-5 w-5" />
              <span className="font-mono text-xl font-bold" data-testid="exam-timer">
                {formatTime(timeLeft)}
              </span>
            </div>
            
            {/* Progress */}
            <div className="text-sm">
              <span className="font-semibold">{answeredCount}</span>
              <span className="text-slate-400"> / {questions.length} Answered</span>
            </div>
            
            {/* Submit Button */}
            <Button 
              onClick={handleSubmitExam}
              className="bg-green-600 hover:bg-green-700"
              data-testid="submit-exam-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Submit Exam
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 flex gap-6">
        {/* Question Panel */}
        <div className="flex-1">
          <div className="bg-white rounded-xl shadow-sm p-6" data-testid="question-panel">
            {/* Question Header */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <span className="bg-blue-600 text-white px-3 py-1 rounded-lg font-bold">
                  Q{currentQuestionIndex + 1}
                </span>
                <span className="text-slate-500">of {questions.length}</span>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={toggleFlag}
                className={flaggedQuestions.has(currentQuestion?.id) ? 'border-orange-500 text-orange-500' : ''}
              >
                <Flag className={`h-4 w-4 ${flaggedQuestions.has(currentQuestion?.id) ? 'fill-orange-500' : ''}`} />
                {flaggedQuestions.has(currentQuestion?.id) ? ' Flagged' : ' Flag for Review'}
              </Button>
            </div>
            
            {/* Question Text */}
            <div className="mb-8">
              <p className="text-xl text-slate-800 leading-relaxed" data-testid="question-text">
                {currentQuestion?.question_text}
              </p>
            </div>
            
            {/* Options */}
            <div className="space-y-3">
              {['A', 'B', 'C', 'D'].map((option) => {
                const optionText = currentQuestion?.[`option_${option.toLowerCase()}`];
                const isSelected = answers[currentQuestion?.id] === option;
                
                return (
                  <button
                    key={option}
                    onClick={() => handleSelectOption(option)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                        : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                    }`}
                    data-testid={`option-${option}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        isSelected ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {option}
                      </span>
                      <span className="flex-1">{optionText}</span>
                      {isSelected && <CheckCircle className="h-5 w-5 text-blue-500" />}
                    </div>
                  </button>
                );
              })}
            </div>
            
            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={handlePrevQuestion}
                disabled={currentQuestionIndex === 0}
                data-testid="prev-question-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>
              
              {currentQuestionIndex < questions.length - 1 ? (
                <Button
                  onClick={handleNextQuestion}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="next-question-btn"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmitExam}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="final-submit-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Submit Exam
                </Button>
              )}
            </div>
          </div>
        </div>
        
        {/* Question Navigator */}
        <div className="w-64">
          <div className="bg-white rounded-xl shadow-sm p-4 sticky top-24">
            <h3 className="font-semibold text-slate-700 mb-4">Question Navigator</h3>
            
            <div className="grid grid-cols-5 gap-2 mb-6">
              {questions.map((q, idx) => {
                const isAnswered = answers[q.id];
                const isCurrent = idx === currentQuestionIndex;
                const isFlagged = flaggedQuestions.has(q.id);
                
                return (
                  <button
                    key={q.id}
                    onClick={() => jumpToQuestion(idx)}
                    className={`w-10 h-10 rounded-lg font-medium text-sm relative ${
                      isCurrent 
                        ? 'bg-blue-600 text-white ring-2 ring-blue-300' 
                        : isAnswered 
                          ? 'bg-green-100 text-green-700 border border-green-300' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid={`nav-question-${idx + 1}`}
                  >
                    {idx + 1}
                    {isFlagged && (
                      <Flag className="absolute -top-1 -right-1 h-3 w-3 text-orange-500 fill-orange-500" />
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Legend */}
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-blue-600"></div>
                <span className="text-slate-600">Current</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-100 border border-green-300"></div>
                <span className="text-slate-600">Answered ({answeredCount})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-slate-100"></div>
                <span className="text-slate-600">Not Answered ({questions.length - answeredCount})</span>
              </div>
              <div className="flex items-center gap-2">
                <Flag className="h-4 w-4 text-orange-500 fill-orange-500" />
                <span className="text-slate-600">Flagged ({flaggedQuestions.size})</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnlineExamPage;
